/**
 * How each sample of a control's test runs is checked.
 *
 * Stored on the control's `sampling_attributes` JSON as `sample_check_mode` (the backend merges keys on save):
 *   auto        line by line only when the sample has a real list (a statement, a population row); otherwise the
 *               sample's documents are checked as one (default)
 *   whole       the sample's documents as one, never split into line items (approvals, duplicate checks, GL coding)
 *   line_items  always line by line, even rows of a table inside a document
 */

export type SampleCheckMode = 'auto' | 'whole' | 'line_items';

export const SAMPLE_CHECK_MODES: { value: SampleCheckMode; label: string; hint: string }[] = [
  {
    value: 'auto',
    label: 'Automatic',
    hint: 'Line by line when a sample is a statement or list line (e.g. card statement + invoices); otherwise as a whole.',
  },
  {
    value: 'whole',
    label: 'As a whole',
    hint: "Each sample's documents are checked together. Tables inside them (fee lines, time entries) are not split out.",
  },
  {
    value: 'line_items',
    label: 'Line by line',
    hint: 'Every line of the sample is matched to its support, including rows of a table inside a document.',
  },
];

export function getSampleCheckMode(control: { sampling_attributes?: unknown } | null | undefined): SampleCheckMode {
  const attrs = control?.sampling_attributes;
  const v = attrs && typeof attrs === 'object' && !Array.isArray(attrs)
    ? (attrs as Record<string, unknown>).sample_check_mode
    : undefined;
  return v === 'whole' || v === 'line_items' ? v : 'auto';
}
